"use client";

import { useEffect, useState } from "react";
import { getDataLayerObject, getSiteId } from "../Analytics/DataLayer.helper";
import type { BuilderContent } from "@builder.io/sdk-react";
import type {
  AnalyticsGlobalField,
  PageProductCategoryGlobalField,
} from "@solarwinds-webbiz/types";
import {
  mapBuilderProduct,
  type EnrichedProductReference,
} from "./mapBuilderProduct";

type Site = "try" | "swdc" | "support" | "itProDay";

interface DataLayerScriptProps {
  locale: "en-us" | "es" | "de" | "fr" | "pt" | "ko" | "zh" | "ja";
  pageAnalytics?: {
    page?: {
      name?: string;
      detail?: string;
      type?: AnalyticsGlobalField["page_type"];
    };
  };
  content: BuilderContent;
  siteName: Site;
  associatedProduct?: EnrichedProductReference;
  searchCategory?: string[];
  pageCategory?: string[];
}
const LANGUAGES_AND_COUNTRIES = {
  "en-us": "US",
  es: "ES",
  de: "DE",
  fr: "FR",
  pt: "PT",
  ko: "KO",
  zh: "CN",
  ja: "JP",
} as const;

const SITE_BRAND: { [key in Site]: string } = {
  swdc: "Solarwinds",
  support: "Success Center",
  try: "Try solarwinds",
  itProDay: "IT Pro Day",
};

const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;

  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(name + "=")) {
      return decodeURIComponent(cookie.substring(name.length + 1));
    }
  }
  return null;
};

const DataLayerScript = ({
  locale,
  pageAnalytics,
  content,
  siteName,
  associatedProduct,
  searchCategory,
  pageCategory,
}: DataLayerScriptProps) => {
  const [variation] = useState(() => {
    return getCookie(`builder.tests.${content?.id}`) || null;
  });
  const localePrefix =
    locale === "en-us" ? "US" : LANGUAGES_AND_COUNTRIES[locale];

  const analyticsData =
    variation && variation !== content?.id
      ? content.variations?.[variation]?.data?.analytics
      : pageAnalytics;

  const dataLayerObj = getDataLayerObject({
    siteId: getSiteId(locale, siteName),
    siteBrand: SITE_BRAND[siteName],
    pageCategory: pageCategory as
      PageProductCategoryGlobalField["category"][] | undefined,
    page: {
      analyticsPageName: analyticsData?.page?.name?.replace(
        /^US/i,
        localePrefix,
      ),
      detail: analyticsData?.page?.detail,
      type: analyticsData?.page?.type,
      categories: searchCategory ?? [],
    },
    ...(variation
      ? {
          personalization: {
            variation:
              variation === content?.id
                ? "Default"
                : content.variations?.[variation]?.name,
          },
        }
      : {}),
    associatedProduct: mapBuilderProduct(associatedProduct),
    language: locale,
    fallbackLanguages: [],
  });

  useEffect(() => {
    window.dataLayer = JSON.parse(JSON.stringify(dataLayerObj));
  }, [dataLayerObj]);

  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `window.dataLayer = ${JSON.stringify(dataLayerObj)};`,
      }}
    />
  );
};

export default DataLayerScript;
